import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Modal } from "bootstrap";

/* TipTap */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import CustomImageExtension from "./CustomImageExtension"; // adjust path if needed

const AdminGetBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const BASE_URL = process.env.REACT_APP_BASE_URL+"/api/blog/image";

  /* -------------------------------------------------- */
  /* Extract preview text from TipTap JSON               */
  /* -------------------------------------------------- */
  const extractTextFromJSON = (jsonString) => {
    try {
      const json = JSON.parse(jsonString);
      let text = "";

      const walk = (node) => {
        if (node.type === "text") text += node.text + " ";
        if (node.content) node.content.forEach(walk);
      };

      walk(json);
      return text.trim();
    } catch {
      return "";
    }
  };

  /* -------------------------------------------------- */
  /* Fetch blogs                                        */
  /* -------------------------------------------------- */
  const fetchBlogs = async () => {
    try {
      const res = await api.get("/api/blog/admins/my-blogs");
      setBlogs(res.data || []);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  /* -------------------------------------------------- */
  /* Backward compatibility for old layouts              */
  /* -------------------------------------------------- */
  const normalizeLayout = (layout) => {
    switch (layout) {
      case "left-wrap":
      case "left-fixed":
      case "inline":
        return "left";
      case "right-wrap":
      case "right-fixed":
        return "right";
      case "top-bottom":
      case "wide":
      case "full-width":
        return "top";
      case "overlay-front":
      case "overlay-back":
        return "overlay";
      default:
        return layout || "left";
    }
  };

  /* -------------------------------------------------- */
  /* TipTap VIEWER (READ ONLY)                           */
  /* -------------------------------------------------- */
  const BlogViewer = ({ content }) => {
    const editor = useEditor({
      editable: false,
      extensions: [
        StarterKit,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        CustomImageExtension,
      ],
      content: JSON.parse(content),
    });

    if (!editor) return null;
    return <EditorContent editor={editor} />;
  };

  /* -------------------------------------------------- */
  /* Layout renderer (keeps previous behavior)           */
  /* -------------------------------------------------- */
  const renderLayout = (blog) => {
    const layout = normalizeLayout(blog.layout);
    const imageUrl = blog.imageFileName
      ? `${BASE_URL}/${blog.imageFileName}`
      : null;

    return (
      <div style={{ width: "100%" }}>
        {/* LEFT */}
        {layout === "left" && (
          <div className="clearfix">
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "330px",
                  float: "left",
                  marginRight: "20px",
                  marginBottom: "20px",
                  borderRadius: "10px",
                }}
                alt=""
              />
            )}
            <BlogViewer content={blog.textContent} />
          </div>
        )}

        {/* RIGHT */}
        {layout === "right" && (
          <div className="clearfix">
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "330px",
                  float: "right",
                  marginLeft: "20px",
                  marginBottom: "20px",
                  borderRadius: "10px",
                }}
                alt=""
              />
            )}
            <BlogViewer content={blog.textContent} />
          </div>
        )}

        {/* TOP */}
        {layout === "top" && (
          <div style={{ textAlign: "center" }}>
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "100%",
                  maxWidth: "700px",
                  borderRadius: "10px",
                  marginBottom: "20px",
                }}
                alt=""
              />
            )}
            <div style={{ maxWidth: "700px", margin: "auto" }}>
              <BlogViewer content={blog.textContent} />
            </div>
          </div>
        )}

        {/* BOTTOM */}
        {layout === "bottom" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ maxWidth: "700px", margin: "auto", marginBottom: "20px" }}>
              <BlogViewer content={blog.textContent} />
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "100%",
                  maxWidth: "700px",
                  borderRadius: "10px",
                }}
                alt=""
              />
            )}
          </div>
        )}

        {/* CENTER */}
        {layout === "center" && (
          <div style={{ textAlign: "center" }}>
            <div style={{ maxWidth: "700px", margin: "auto" }}>
              <BlogViewer content={blog.textContent} />
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "350px",
                  borderRadius: "10px",
                  marginTop: "20px",
                }}
                alt=""
              />
            )}
          </div>
        )}

        {/* OVERLAY */}
        {layout === "overlay" && (
          <div style={{ position: "relative", padding: "20px" }}>
            <div style={{ maxWidth: "700px" }}>
              <BlogViewer content={blog.textContent} />
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                style={{
                  width: "230px",
                  position: "absolute",
                  bottom: "20px",
                  right: "20px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                }}
                alt=""
              />
            )}
          </div>
        )}
      </div>
    );
  };

  /* -------------------------------------------------- */
  /* RENDER                                             */
  /* -------------------------------------------------- */
  return (
    <div className="container my-5">
      {loading ? (
        <p>Loading blogs...</p>
      ) : blogs.length > 0 ? (
        <div className="row g-4">
          {blogs.map((blog) => (
            <div key={blog.id} className="col-md-6 col-lg-4">
              <div className="card h-100 shadow-sm border-0 rounded-4 overflow-hidden">
                {blog.imageFileName ? (
                  <img
                    src={`${BASE_URL}/${blog.imageFileName}`}
                    alt={blog.title}
                    className="card-img-top"
                    style={{ height: "200px", objectFit: "cover" }}
                    onError={(e) => (e.target.style.display = "none")}
                  />
                ) : (
                  <div 
                    className="bg-light d-flex align-items-center justify-content-center"
                    style={{ height: "200px" }}
                  >
                    <span className="text-muted">No Image</span>
                  </div>
                )}

                <div className="card-body">
                  <h5 className="fw-semibold">{blog.title}</h5>
                  <p className="text-muted small">
                    {extractTextFromJSON(blog.textContent).slice(0, 80)}…
                  </p>

                  <button
                    className="btn btn-outline-primary btn-sm rounded-pill px-3 mt-2"
                    onClick={() => {
                      setSelectedBlog(blog);
                      setTimeout(() => {
                        const modal = new Modal(
                          document.getElementById("blogModal")
                        );
                        modal.show();
                      }, 50);
                    }}
                  >
                    👁 View
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-center mt-5">No blogs available.</p>
      )}

      {/* VIEW MODAL */}
      <div className="modal fade" id="blogModal" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div
            className="modal-content rounded-4 shadow-lg border-0"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            {selectedBlog && (
              <>
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">{selectedBlog.title}</h5>
                  <button
                    className="btn-close btn-close-white"
                    data-bs-dismiss="modal"
                  />
                </div>

                <div className="modal-body">
                  {renderLayout(selectedBlog)}
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" data-bs-dismiss="modal">
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGetBlogs;
